import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import type { ProductId } from "./productTypes";

const PRODUCTS: { id: ProductId; label: string; color: string }[] = [
  { id: "field-service", label: "Makula Field Service", color: "#0517F8" },
  { id: "cmms", label: "Makula CMMS", color: "#FF7A00" },
  { id: "customer-portal", label: "Makula Customer Portal", color: "var(--color-bg-danger)" },
];

function HexagonIcon({ color }: { color: string }) {
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: "var(--radius-xs)",
        border: "1px solid var(--color-border-primary)",
        background: "var(--color-bg-primary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <svg width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.7239 0.564799L14.7649 2.77514C16.1477 3.53149 17 4.93245 17 6.44896V13.551C17 15.0675 16.1477 16.4685 14.7649 17.2249L10.7239 19.4352C9.34713 20.1883 7.65287 20.1883 6.2761 19.4352L2.23511 17.2249C0.852329 16.4685 0 15.0675 0 13.551V6.44896C0 4.93245 0.852329 3.53149 2.23511 2.77514L6.2761 0.564799C7.65287 -0.188266 9.34713 -0.188266 10.7239 0.564799ZM6.35687 4.42203L6.31495 4.44475L2.78922 6.37988C1.45079 7.1145 0.62115 8.46582 0.604692 9.93267L0.604442 9.97715V10.0228C0.604442 11.492 1.42069 12.8506 2.74882 13.5977L2.78922 13.6201L6.31495 15.5552C7.65311 16.2897 9.29864 16.2973 10.6431 15.578L10.6851 15.5552L14.2108 13.6201C15.5492 12.8855 16.3789 11.5342 16.3953 10.0673L16.3956 10.0228V9.97715C16.3956 8.50802 15.5793 7.14935 14.2512 6.40233L14.2108 6.37988L10.6851 4.44475C9.34689 3.71029 7.70136 3.70271 6.35687 4.42203Z" fill={color} />
      </svg>
    </div>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
      <path d="M5 8l5 5 5-5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-brand)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8.5l3.5 3.5L13 5" />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-secondary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="5" height="5" rx="1" />
      <rect x="9" y="2" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="5" rx="1" />
      <rect x="9" y="9" width="5" height="5" rx="1" />
    </svg>
  );
}

interface WorkspaceSwitcherProps {
  activeProductId: ProductId;
  onSelectProduct: (id: ProductId) => void;
  onOpenDashboard: () => void;
}

export function WorkspaceSwitcher({ activeProductId, onSelectProduct, onOpenDashboard }: WorkspaceSwitcherProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const active = PRODUCTS.find((p) => p.id === activeProductId) ?? PRODUCTS[0];

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const rowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "var(--spacing-s)",
    width: "100%",
    padding: "var(--spacing-xs) var(--spacing-s)",
    background: "none",
    border: "none",
    cursor: "pointer",
    borderRadius: "var(--radius-s)",
    fontFamily: "var(--font-family-base)",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--color-content-primary)",
    textAlign: "left",
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-s)",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          width: "100%",
          textAlign: "left",
        }}
      >
        <HexagonIcon color={active.color} />
        <span
          style={{
            flex: 1,
            fontFamily: "var(--font-family-base)",
            fontSize: 14,
            fontWeight: 700,
            lineHeight: "20px",
            letterSpacing: "-0.28px",
            color: "var(--color-content-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {active.label}
        </span>
        <ChevronDownIcon />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            zIndex: 50,
            background: "var(--color-bg-primary)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: "var(--radius-m)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: "var(--spacing-xs)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-3xs)",
          }}
        >
          {PRODUCTS.map((product) => (
            <button
              key={product.id}
              onClick={() => { onSelectProduct(product.id); setOpen(false); }}
              style={{
                ...rowStyle,
                background: product.id === activeProductId ? "var(--color-bg-accent)" : "none",
              }}
            >
              <HexagonIcon color={product.color} />
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {product.label}
              </span>
              {product.id === activeProductId && <CheckIcon />}
            </button>
          ))}

          <div style={{ height: 1, background: "var(--color-border-primary)", margin: "var(--spacing-2xs) 0" }} />

          <button
            onClick={() => { onOpenDashboard(); setOpen(false); }}
            style={rowStyle}
          >
            <span
              style={{
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DashboardIcon />
            </span>
            <span style={{ flex: 1 }}>Dashboard</span>
          </button>
        </div>
      )}
    </div>
  );
}
